import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import PresentationAttributeApi from '@/api/presentation-attribute'
import constant from '@/constant'
import { isExpectedTransientResponse } from '@/3rd-parties/sentry'

const SYSTEM_FIELDS = 'systemField'
const participantSystemField = [
  {
    name: 'name',
    type: 'name',
    fieldType: SYSTEM_FIELDS,
    label: 'Enter your name:',
    required: false,
    id: 1,
  },
  {
    name: 'email',
    type: 'email',
    fieldType: SYSTEM_FIELDS,
    label: 'Enter your email address:',
    required: false,
    id: 3,
  },
  {
    name: 'avatar',
    type: 'avatar',
    fieldType: SYSTEM_FIELDS,
    label: 'Choose an emoji as your avatar:',
    required: false,
    id: 11,
  },
]

const _state = {
  presentationAttribute: {},
}

const mutations = {
  setPresentationAttribute(state, payload) {
    state.presentationAttribute = payload
  },
  updatePresentationAttribute(state, payload) {
    Vue.set(state.presentationAttribute, payload.attributeName, payload.attributeValue)
  },
}

const actions = {
  async getPresentationAttributeAction({ commit }, presentationId) {
    try {
      const resp = await PresentationAttributeApi.getPresentationAttributes(presentationId)
      commit('setPresentationAttribute', resp.body)
      return resp.body
    } catch (error) {
      if (!isExpectedTransientResponse(error)) {
        // Pass the raw error (not wrapped) so beforeSend's classifyEvent can see
        // a genuine vue-resource Response and relabel it into one groupable
        // HttpResponseError signal instead of an unactionable `<object>.value`.
        Sentry.captureException(error, { extra: { presentationId } })
      }
      return {}
    }
  },
}

const _getters = {
  presentationColorPalette: (state) => {
    if (state.presentationAttribute?.colorPalette?.colors) {
      return state.presentationAttribute.colorPalette.colors
    }
    return constant.colors2025
  },
  presentationLighterColorPalette: (state) => {
    if (state.presentationAttribute?.colorPalette?.lighterColors) {
      return state.presentationAttribute.colorPalette.lighterColors
    }
    return constant.lighterColors2025
  },

  presentationAttributeColorPalette: (state) => {
    return state.presentationAttribute.colorPalette || {}
  },
  presentationParticipantFields: (state) => {
    return [
      ...participantSystemField,
      ...(state.presentationAttribute?.participantFields?.fields || []),
    ]
  },
  // Drives the force-login gate in Presentation.vue. Backend stores this as a
  // PresentationAttribute row with attributeName = 'participantVerification' and a JSONB
  // attributeValue (extensible namespace — future fields expected: allowedDomains,
  // ssoProvider, etc.). Strict `=== true` comparison is intentional: any missing/falsy/
  // non-boolean value defaults to "off" (fail-open), mirroring how the existing attribute
  // fetch swallows errors.
  isParticipantVerificationEnabled: (state) => {
    return state.presentationAttribute?.participantVerification?.enabled === true
  },
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
